import React, { useState } from 'react';
import {
  Amount,
  Box,
  Button,
  Divider,
  EditIcon,
  Heading,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
  useToast,
} from '@razorpay/blade/components';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
} from 'components/WizardViews/components';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import { useFormWithProvider } from 'forms';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import { AdminCreateLoanFormData, SuccessModalProps } from './types';
import { BasicUserLoanData } from 'components/LoanPaymentV2/types';
import { formatMonthYear } from '../utils';
import { BASIC_USER_LOAN_DATA_KEY, LOAN_CALC_PREVIEW_KEY } from './constants';
import loanRequestSuccess from 'assets/loan/loan-request-success.png';
import { INTEREST_TYPE_LABEL } from 'components/LoanPaymentV2/constants';

const ReviewRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center">
    <Text size="small" weight="semibold" color="surface.text.gray.muted">
      {label}
    </Text>
    <Text size="small" color="surface.text.gray.subtle">
      {value}
    </Text>
  </Box>
);

const SuccessModal = ({ isOpen, loanId, isAlreadyPaid, onClose }: SuccessModalProps) => (
  <Modal isOpen={isOpen} onDismiss={onClose} size="small">
    <ModalHeader />
    <ModalBody>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap="spacing.6"
        paddingY="spacing.4"
        textAlign="center">
        <img src={loanRequestSuccess} alt="Loan Created" width={72} height={72} />
        {loanId && (
          <Text size="small" weight="medium" color="surface.text.gray.muted">
            Loan ID #{loanId}
          </Text>
        )}
        <Box display="flex" flexDirection="column" gap="spacing.2">
          <Heading size="medium" weight="semibold" color="surface.text.gray.normal">
            {isAlreadyPaid ? 'Already Settled!' : 'Loan Created!'}
          </Heading>
          <Text size="small" color="surface.text.gray.subtle">
            {isAlreadyPaid
              ? 'This loan was paid outside Razorpay. No disbursement is required.'
              : 'Your loan is now Pending Disbursement.'}
          </Text>
        </Box>
        {!isAlreadyPaid ? (
          <Text size="small" color="surface.text.gray.muted">
            View the loan in the Pending Disbursement section and disburse it from there.
          </Text>
        ) : null}
      </Box>
    </ModalBody>
    <ModalFooter>
      <Button isFullWidth variant="secondary" onClick={onClose}>
        Close
      </Button>
    </ModalFooter>
  </Modal>
);

export const ReviewRequest = () => {
  const { setCurrentStep, onSuccess } = useWizardInfo();
  const { formData } = useFormWithProvider<AdminCreateLoanFormData>();
  const { show: showToast } = useToast();
  const queryClient = useQueryClient();
  const [createdLoanId, setCreatedLoanId] = useState<number | null>(null);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const isInterestFree = formData.interestType === '4';
  const apiInterestType = Number(formData.interestType);

  const { data: basicUserData } = useQuery<BasicUserLoanData>({
    queryKey: [BASIC_USER_LOAN_DATA_KEY, formData.employee?.people_id, formData.recoveryStartFrom],
    queryFn: () =>
      api.loans.basicUserLoanData(
        formData.employee!.people_id,
        formData.recoveryStartFrom ?? undefined,
      ),
    enabled: !!formData.employee?.people_id,
  });

  const { data: calcData } = useQuery({
    queryKey: [
      LOAN_CALC_PREVIEW_KEY,
      formData.principal,
      formData.tenure,
      formData.interestType,
      formData.interestRate,
    ],
    queryFn: () =>
      api.loans.calculateLoan({
        principal: Number(formData.principal),
        tenure: formData.tenure ?? 1,
        interest_type: apiInterestType,
        interest_rate: Number(formData.interestRate || 0),
        perk_rate: Number(formData.perkRate || 0),
      }),
    enabled: !!formData.principal && !!formData.tenure,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.loans.createLoan({
        user_id: formData.employee!.people_id,
        principal: Number(formData.principal),
        interest_rate: Number(formData.interestRate || 0),
        perk_rate: Number(formData.perkRate || 0),
        tenure: formData.tenure ?? 1,
        remarks: formData.remarks || formData.categoryName || 'Loan Request',
        interest_type: apiInterestType,
        recovery_start_from: formData.recoveryStartFrom ?? '',
        loan_category_id: formData.loanCategoryId ?? undefined,
        is_already_paid: formData.isAlreadyPaid,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['LOANS']);
      const loanId = Array.isArray(response) ? response[0]?.id : (response as { id?: number })?.id;
      setCreatedLoanId(loanId ?? null);
      setIsSuccessOpen(true);
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to create loan. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const handleSuccessClose = () => {
    setIsSuccessOpen(false);
    showToast({
      content: formData.isAlreadyPaid
        ? 'Loan recorded as already settled.'
        : 'Loan created and moved to Pending Disbursements',
      color: 'positive',
      duration: 4000,
    });
    onSuccess?.();
  };

  const monthlySalary =
    basicUserData?.salary != null ? Math.round(basicUserData.salary / 12) : null;
  const newMonthlyEmi = calcData?.monthly_total_emi ?? null;
  const estNewTakeHome =
    monthlySalary != null && newMonthlyEmi != null ? monthlySalary - newMonthlyEmi : null;

  return (
    <>
      <Box display="flex" flex={1} overflow="hidden">
        <FormWrapper
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
          style={{ flex: 1, minWidth: 0 }}>
          <StepContentWrapper noBgIllustration width="462px">
            <Box display="flex" flexDirection="column" gap="spacing.7">
              <Box>
                <Heading size="large" weight="semibold" color="surface.text.gray.normal">
                  Review Request
                </Heading>
                <Text size="medium" color="surface.text.gray.muted" marginTop="spacing.2">
                  Review the details before creation
                </Text>
              </Box>

              <Box
                display="flex"
                flexDirection="column"
                borderWidth="thin"
                borderRadius="medium"
                borderColor="surface.border.gray.muted"
                padding="spacing.7"
                gap="spacing.5">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  marginBottom="spacing.3">
                  <Box>
                    <Text size="small" weight="semibold" color="surface.text.gray.muted">
                      Amount
                    </Text>
                    <Amount value={Number(formData.principal || 0)} size="large" />
                  </Box>
                  <Link
                    color="primary"
                    size="medium"
                    icon={EditIcon}
                    onClick={() => setCurrentStep(1)}>
                    Edit
                  </Link>
                </Box>
                <Divider />

                <ReviewRow
                  label="Employee"
                  value={`${formData.employee?.name ?? '—'} (${
                    formData.employee?.employee_id ?? '—'
                  })`}
                />
                <ReviewRow label="Reason" value={formData.categoryName || '—'} />
                <ReviewRow
                  label="Payment Status"
                  value={formData.isAlreadyPaid ? 'Already Paid' : 'To be paid'}
                />
                <ReviewRow
                  label="Interest Type"
                  value={
                    formData.interestType
                      ? INTEREST_TYPE_LABEL[Number(formData.interestType)] ?? '—'
                      : '—'
                  }
                />
                {!isInterestFree && formData.interestRate && (
                  <ReviewRow
                    label="Interest Rate (Per Annum)"
                    value={`${formData.interestRate}%`}
                  />
                )}
                {!isInterestFree && formData.perkRate && (
                  <ReviewRow label="Perquisite Rate" value={`${formData.perkRate}%`} />
                )}
                <ReviewRow label="Repayment Tenure" value={`${formData.tenure} Months`} />
                <ReviewRow
                  label="EMI Deducts From"
                  value={formatMonthYear(formData.recoveryStartFrom)}
                />
              </Box>
            </Box>
          </StepContentWrapper>

          <StepActionsWrapper width="462px">
            <Button variant="secondary" onClick={() => setCurrentStep(2)}>
              Previous
            </Button>
            <Button type="submit" isLoading={createMutation.isLoading}>
              Create
            </Button>
          </StepActionsWrapper>
        </FormWrapper>

        <Box
          width="380px"
          height="100%"
          borderLeftWidth="thin"
          borderColor="surface.border.gray.subtle"
          backgroundColor="surface.background.gray.moderate"
          flexShrink={0}>
          <Box padding="spacing.7" display="flex" flexDirection="column" gap="spacing.5">
            <Text size="small" weight="semibold" color="surface.text.gray.normal">
              Preview
            </Text>
            <Box display="flex" flexDirection="column" gap="spacing.4">
              {[
                { label: 'Total Repayment Amount', value: calcData?.total_amount },
                { label: 'Monthly EMI', value: calcData?.monthly_total_emi },
                { label: 'Total Interest Amount', value: calcData?.total_interest },
              ].map(({ label, value }) => (
                <Box key={label} display="flex" justifyContent="space-between" alignItems="center">
                  <Text size="small" color="surface.text.gray.muted">
                    {label}
                  </Text>
                  {value != null ? (
                    <Amount value={value} size="small" />
                  ) : (
                    <Text size="small">—</Text>
                  )}
                </Box>
              ))}
            </Box>
            {!formData.isAlreadyPaid && (
              <>
                <Divider marginY="spacing.3" />
                <Box display="flex" flexDirection="column" gap="spacing.5">
                  <Text size="small" weight="semibold" color="surface.text.gray.normal">
                    Salary Impact Analysis
                  </Text>
                  {[
                    { label: 'Current Monthly Salary', value: monthlySalary },
                    {
                      label: 'Existing EMIs',
                      value: basicUserData?.total_recovery_for_month ?? null,
                    },
                    { label: 'New Monthly EMI', value: newMonthlyEmi },
                  ].map(({ label, value }) => (
                    <Box
                      key={label}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center">
                      <Text size="small" color="surface.text.gray.muted">
                        {label}
                      </Text>
                      {value != null ? (
                        <Amount value={value} size="small" />
                      ) : (
                        <Text size="small">—</Text>
                      )}
                    </Box>
                  ))}
                </Box>
                <Divider dividerStyle="dashed" marginY="spacing.3" />
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Text size="small" color="surface.text.gray.muted">
                    Est. New Take Home
                  </Text>
                  {estNewTakeHome != null ? (
                    <Amount value={estNewTakeHome} size="small" />
                  ) : (
                    <Text size="small">—</Text>
                  )}
                </Box>
              </>
            )}
            <Divider marginY="spacing.3" />
            <Text size="medium" weight="medium" color="surface.text.gray.subtle">
              Next Steps
            </Text>
            <Text size="small" color="surface.text.gray.muted">
              {formData.isAlreadyPaid
                ? 'This loan has been recorded as already settled. No disbursement is required.'
                : 'This loan will be moved to pending disbursements after being created, you can then disburse the amount from there.'}
            </Text>
          </Box>
        </Box>
      </Box>

      <SuccessModal
        isOpen={isSuccessOpen}
        loanId={createdLoanId}
        isAlreadyPaid={formData.isAlreadyPaid ?? false}
        onClose={handleSuccessClose}
      />
    </>
  );
};
